/**
 * Danger zone — account deletion with soft-delete + PII anonymization.
 *
 * Flow:
 *   1. User clicks "Delete account"
 *   2. Confirmation modal — user must type "DELETE MY ACCOUNT"
 *   3. DELETE /v1/account/me is called
 *   4. On success: logout → redirect to /
 *
 * What the backend does:
 *   - Anonymizes email/name/phone/avatar/username
 *   - Sets status=DELETED, isBlocked=true, deletedAt=now
 *   - Disables published invitations (public URLs go dark)
 *   - Soft-deletes drafts
 *   - Revokes active sessions
 *   - Preserves financial records (invoices, billing payments)
 *   - Frees the email address for re-registration
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icons } from '@/src/modules/components/Icons';
import { useAuth } from '@/src/modules/auth/AuthProvider';
import { SettingsCard } from '@/src/modules/components/settings/primitives';
import { ConfirmModal } from '@/src/modules/components/settings/ConfirmModal';
import { useToast } from '@/src/modules/components/settings/ToastProvider';
import { deleteAccount } from '@/src/infrastructure/lib/api';

export function DangerZoneSection() {
  const { logout } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <SettingsCard className="border-status-failed/30">
      <div className="flex items-start gap-3 mb-1">
        <span className="w-9 h-9 shrink-0 rounded-xl bg-status-failed/10 text-status-failed flex items-center justify-center">
          <Icons.Warning size={18} />
        </span>
        <div>
          <h3 className="text-base font-bold text-status-failed">Danger zone</h3>
          <p className="text-sm text-on-surface-variant">
            These actions are permanent and cannot be undone.
          </p>
        </div>
      </div>

      <div className="mt-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-4">
          <div className="pr-4">
            <p className="text-sm font-bold text-on-surface">Delete account</p>
            <p className="text-xs text-on-surface-variant mt-0.5 max-w-md">
              Permanently deactivates your account, anonymizes your personal data, and
              disables all published invitations. Financial records are retained for
              legal and tax purposes. Your email address will be freed for re-registration.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="shrink-0 rounded-xl border border-status-failed/40 px-4 py-2 text-sm font-bold text-status-failed hover:bg-status-failed hover:text-white transition-colors active:scale-95"
          >
            Delete account
          </button>
        </div>
      </div>

      <ConfirmModal
        open={open}
        onClose={() => setOpen(false)}
        title="Delete your account?"
        description="This will permanently deactivate your account, anonymize your personal data (name, email, phone), and disable all published invitations. Financial records are preserved. Your original email address will be available for a new registration. This cannot be undone."
        confirmLabel="Delete my account"
        confirmPhrase="DELETE MY ACCOUNT"
        onConfirm={async () => {
          try {
            await deleteAccount();
          } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Something went wrong';
            toast(message, 'error');
            throw err;
          }
          // Confirm success to the user. The toast lives on the app-root
          // ToastProvider, so it survives the logout + redirect that follow.
          // Their email/Google identity is now freed — signing in again creates
          // a brand-new fresh account.
          toast('Account has been deleted successfully', 'success');
          await logout().catch(() => undefined);
          navigate('/', { replace: true });
        }}
      />
    </SettingsCard>
  );
}
